import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditLesson } from '../../useEditLesson';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { useFeedbackForm } from './feedback-form-editor/useFeddbackFormEdit';
import {
  useGetUrlLazyQuery,
  UpdateVideoContentInput, Video,
} from '@api/graphql-codegen';
import { handleUploadVideo } from '@services/Video';
import { useSchoolGroupsModuleCRUD } from './useSchoolGroupsModuleCRUD';
import { useSchoolModuleCRUD } from './useSchoolModuleCRUD';
import { sleep } from '@utils/helpers';
import { SllModule, ContentType, DefaultModuleEnum, ExtendedSllModule } from '@types';
import { logger } from '../../../../../utils/logger';
import { useToast } from '@src/hooks';

export const emptyErrors = {
  name: '',
  position: '',
  feedbackForm: '',
};

export type ErrorKeys = keyof typeof emptyErrors;

export const useEditContent = () => {
  const { content, lesson, isPreparation } = useEditLesson();
  const [customLoading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const [fileUploadFinished, setFileUploadFinished] = useState<boolean>(false);
  const [name, setName] = useState('');
  const [data, setData] = useState<string | null>(null);
  const [formData, setFormData] = useState<string>('');
  const [groups, setGroups] = useState<Array<ExtendedSllModule>>([]);
  const [pauseHere, setPauseHere] = useState<boolean>(false);
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [isLastLesson, setIsLastLesson] = useState<boolean>(false);
  const [isAdditionalAfter, setIsAdditionalAfter] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(groups[0]?.['groupName'] || DefaultModuleEnum.Default);
  const dataInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);
  const { getFormState, hasError: hasFeedbackFormErrors } = useFeedbackForm();
  const { errorToast } = useToast();

  const [getUrl, { loading: urlLoading }] = useGetUrlLazyQuery({
    fetchPolicy: 'network-only',
  });
  const isTeacherPreparationModule = useMemo(() => !!isPreparation && content?.__typename === 'SLLModule', [content?.__typename, isPreparation]);
  const defaultGroupModule: ExtendedSllModule | undefined = useMemo(() => (content?.id ? {...content, contentId: content?.id, groupName: DefaultModuleEnum.Default, groupId: '', type: content.type as ContentType} : undefined), [content]);

  const defaultSchoolContentGroups: ExtendedSllModule[] = useMemo(() =>
      defaultGroupModule ? [defaultGroupModule] : []
    , [defaultGroupModule]);

  const groupOptions = useQueryDictionary({ dictionary: 'ProgramTypes' });

  const availableGroupOptions = useMemo(() => {
    const existingGroups = groups.map(g => g.groupId || DefaultModuleEnum.Default);
    const options = groupOptions;
    return options.filter(g => !existingGroups.includes(g.value));
  }, [groupOptions, groups]);
  const currentContent = useMemo(() => groups.find(g => g.groupName === activeTab), [activeTab, groups]);
  const isGroupContent = useMemo(() => activeTab !== DefaultModuleEnum.Default, [activeTab]);
  /* ------------------------------------------------------------------------------------------------------------------------------------------------------------ */

  const onChangeData = useCallback((value: string | null) => {
    setData(value);
  }, []);

  const onChangeGroupTabs = useCallback((value: ExtendedSllModule[] | ((v: ExtendedSllModule[]) => ExtendedSllModule[])) => {
    setGroups(value);
  }, []);

  const clearFile = useCallback(() => {
    if (dataInputRef.current) {
      dataInputRef.current.value = '';
    }
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
  }, []);

  const updateLocalContent = useCallback((groupContent?: ExtendedSllModule | SllModule, currentActiveTab?: string) => {
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
    setPauseHere(groupContent?.pauseHere ?? false);
    if (groupContent?.type === ContentType.FORM) {
      setFormData(groupContent?.formData ?? '');
      setData('');
    } else {
      setData(groupContent?.data ?? '');
    }
  }, []);

  const onChangeTab = useCallback((key: string) => {
    clearFile();
    setActiveTab(key);
    if (groups?.length > 0) {
      const currentContentByGroup = groups.find(g => g.groupName === key);
      if (currentContentByGroup) {
        updateLocalContent(currentContentByGroup, key);
      }
    }
  }, [clearFile, groups, updateLocalContent]);

  const {
    updateContent,
    updateVideoContent,
    deleteModuleData,
    removeContent,
    isRemovingModule,
    deletingModuleData,
    loading: schoolModuleLoading
  } = useSchoolModuleCRUD({ currentTabContent: currentContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs });

  const {
    getContentsByGroup,
    createNewGroupContentMutation,
    updateGroupContent,
    deleteGroupModuleData,
    deleteSLLGroup,
    deleteSLLGroupContent,
    deleteGroupContent,
    updateVideoGroupContent,
    isGroupRemoving: isSchoolGroupRemoving,
    isGroupsLoading,
    handleRemoveGroup,
    isOpenAddModal,
    handleAddGroup,
    onCloseAddGroupModal,
  } = useSchoolGroupsModuleCRUD({ currentTabContent: currentContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs, onChangeTab });

  /* ------------------------------------------------------------------------------------------------------------------------------------------------------------ */

  const onAddGroup = useCallback(async (groupName: string, groupId: string) => {
    onCloseAddGroupModal();
    if (currentContent?.id) {
      setData('');
      setFormData('');
      const newGroupContent = { id: currentContent.id, contentId: currentContent.id, type: currentContent.type, name: currentContent.name, groupName, groupId } as ExtendedSllModule;
      if (currentContent.type === ContentType.FORM) {
        newGroupContent.formData = currentContent.formData;
      }
      setGroups(groups => ([...groups, newGroupContent]));
      const result = await createNewGroupContentMutation({
        variables: {
          input: { moduleId: currentContent.id, groupId, groupName }
        }
      });
      if (result?.data?.sllCreateGroupModule?.groupName) {
        // setGroups(tabs => ([...tabs, newGroupContent]));
        if (newGroupContent.formData) {
          setFormData(newGroupContent.formData);
        }
        onChangeTab(groupName);
      }
    }
  }, [onCloseAddGroupModal, currentContent, onChangeTab, createNewGroupContentMutation]);
  const getModuleContentsByGroup = useCallback(async (moduleId: string) => {
    const contentsForSchool = await getContentsByGroup({ variables: { input: { id: moduleId } } });
    const groupsDataForSchool = contentsForSchool?.data?.sllGetGroupModules?.groups || [];
    if (groupsDataForSchool?.length) {
      const newGroups: ExtendedSllModule[] = groupsDataForSchool
        .reduce((previous, current) =>
          current?.groupName !== DefaultModuleEnum.Default ? [...previous, ({...content, ...current}) as ExtendedSllModule] : previous
        , defaultSchoolContentGroups) || [];
      setGroups(newGroups);
    } else {
      setGroups(defaultSchoolContentGroups);
    }
  }, [getContentsByGroup, defaultSchoolContentGroups, content]);

  useEffect(() => {
    if (content?.id) {
      if (content.type !== ContentType.PARENT) {
        getModuleContentsByGroup(content?.id);
      }
      clearFile();
      setName(content?.name);
      setFileUploadingPercent(0);
      setFileUploadFinished(false);
      setPauseHere(content?.pauseHere ?? false);
      setIsRequired(content?.isRequired ?? false);
      setIsLastLesson(content?.lastInLesson ?? false);
      setIsAdditionalAfter(content.additionalAfter ?? false);
      if (content?.type === ContentType.FORM) {
        setFormData(content?.formData ?? '');
        setData(content?.data ?? '');
      } else {
        setData(content?.data ?? '');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFile, content?.id]);

  useEffect(() => {
    clearFile();
    if (content?.id) {
      setActiveTab(DefaultModuleEnum.Default);
      setGroups(defaultSchoolContentGroups);
    }
  }, [clearFile, content?.id, defaultSchoolContentGroups]);

  const updateVideoContentFunc = useCallback(async (input: UpdateVideoContentInput, group?: string) => {
    if (group) {
      setLoading(true);
      try {
        let result;
        while (!result?.data?.sllUpdateVideoGroupModule?.data) {
          result = await updateVideoGroupContent({ variables: { input: {
            moduleId: input.id,
            uploadId: input.uploadId,
            data: input.data,
            groupId: group,
          } } });
        }
      } catch (e) {
        logger.info('e---', e);
        await updateVideoGroupContent({ variables: { input: {
          moduleId: input.id,
          uploadId: input.uploadId,
          data: input.data,
          groupId: group,
        } } });
      }
      setLoading(false);
    } else if (input) {
      const { position: __, ...customInputs } = input;
      try {
        await updateVideoContent({ variables: { input: customInputs }});
      } catch (e) {
        logger.info('e1---', e);
        await updateVideoContent({ variables: { input: customInputs }});
      }
    }
    setLoading(false);
    setFileUploadingPercent(0);
    setFileUploadFinished(false);
  }, [updateVideoContent, updateVideoGroupContent]);

  const onProgress = useCallback((percent: number) => {
    setFileUploadingPercent(percent)
  }, [setFileUploadingPercent]);

  const uploadVideoAndSave = useCallback(async (dataFile: File, data: { url: string; uploadId: string }) => {
    if (currentContent!.type === ContentType.VIDEO) {
      setLoading(true);
      if (dataFile && data.url && data.uploadId) {
        await handleUploadVideo(dataFile, data.url, onProgress)
        setFileLoading(false);
        setFileUploadFinished(true);
        await sleep(1000);
        await updateVideoContentFunc({
          id: currentContent!.id,
          type: currentContent!.type,
          name,
          position: groups[0]?.position ?? 1,
          pauseHere: pauseHere,
          lastInLesson: isLastLesson,
          uploadId: data.uploadId,
          data: data.url,
        }, isGroupContent ? currentContent?.groupId : undefined);
      }
      setLoading(false);
    }
  }, [currentContent, onProgress, updateVideoContentFunc, name, groups, pauseHere, isLastLesson, isGroupContent]);

  const handleChangeVideo = useCallback(async (video: Video) => {
    if (currentContent!.type === ContentType.VIDEO) {
      setLoading(true);
      setFileLoading(false);
      setFileUploadFinished(true);
      if (video) {
        await updateVideoContentFunc({
          id: currentContent!.id,
          type: currentContent!.type,
          name,
          position: groups[0]?.position ?? 1,
          pauseHere: pauseHere,
          lastInLesson: isLastLesson,
          uploadId: '',
          data: video.data,
          meta: video.meta
        }, isGroupContent ? currentContent?.groupId : undefined);
      }
      setLoading(false);
    }
  }, [currentContent, updateVideoContentFunc, name, groups, pauseHere, isLastLesson, isGroupContent]);

  const handleSave = useCallback(async () => {
    if (customLoading || fileLoading || !currentContent) return;
    const errors: Partial<Record<ErrorKeys, string>> = {};

    let updatedFormData = formData;
    if (currentContent?.type === ContentType.FORM) {
      if (hasFeedbackFormErrors) {
        errors.feedbackForm = 'Feed feedback form'
        setErrors(errors);
        errorToast('Cannot save lesson. Please check that all Required answers are selected.');
        return;
      }
      updatedFormData = JSON.stringify(getFormState());
    }
    setLoading(true);
    if (isGroupContent) {
      if (currentContent?.id && currentContent?.groupId) {
        await Promise.all([
          updateContent({
            variables: {
              input: {
                id: currentContent!.id,
                type: currentContent!.type as ContentType,
                name,
              }
            }
          }),
          updateGroupContent({
            variables: {
              input: {
                moduleId: currentContent?.id,
                groupId: currentContent?.groupId,
                data: data || '',
                formData: updatedFormData,
              }
            }
          }),
        ]);
      }
    } else {
      await updateContent({
        variables: {
          input: {
            id: currentContent!.id,
            type: currentContent!.type as ContentType,
            name,
            pauseHere: pauseHere,
            lastInLesson: isLastLesson,
            additionalAfter: isAdditionalAfter,
            data: data || '',
            formData: updatedFormData,
            isRequired: isRequired,
          }
        }
      });
    }
    setLoading(false);
  }, [
    customLoading,
    fileLoading,
    currentContent,
    formData,
    isGroupContent,
    hasFeedbackFormErrors,
    name,
    data,
    pauseHere,
    isLastLesson,
    isAdditionalAfter,
    isRequired,
    updateGroupContent,
    getFormState,
    errorToast,
    updateContent,
  ]);

  const handleContentDataSave = useCallback(async (data: string) => {
    if (!currentContent) return;
    setLoading(true);
    if (isGroupContent) {
      await updateGroupContent({
        variables: {
          input: {
            moduleId: currentContent?.id,
            groupId: currentContent?.groupId,
            data,
          }
        }
      });
    } else {
      await updateContent({
        variables: {
          input: {
            id: currentContent!.id,
            type: currentContent!.type as ContentType,
            name,
            data,
            pauseHere: pauseHere,
            lastInLesson: isLastLesson,
            additionalAfter: isAdditionalAfter,
            isRequired, 
          }
        }
      });
    }
    setLoading(false);
  }, [currentContent, isGroupContent, isLastLesson, isAdditionalAfter, name, pauseHere, isRequired, updateContent, updateGroupContent]);

  const handleRemoveModule = useCallback(async () => {
    if (window.confirm('Are you sure you want to remove the Module?')) {
      setLoading(true);
      if (currentContent?.id) {
        await removeContent({
          variables: {
            input: { id: currentContent!.id },
          }
        });

        const existingGroups: ExtendedSllModule[] = groups.slice(1);
        if (existingGroups.length > 0) {
          await Promise.all(existingGroups.map(group => {
              return Promise.all([
                deleteGroupContent({
                  variables: {
                    input: {
                      moduleId: currentContent.id,
                      groupId: group?.groupId
                    }
                  }
                })
              ])
            }))
          }
      }
      setLoading(false);
    }
  }, [currentContent, groups, removeContent, deleteGroupContent]);

  const onChangeRequiredLesson = useCallback((value: boolean) => {
    setIsRequired(value);
  }, []);

  const onChangeLastLessonMark = useCallback((value: boolean) => {
    setIsLastLesson(value);
  }, []);

  const onChangeAdditionalAfter = useCallback((value: boolean) => {
    setIsAdditionalAfter(value);
  }, []);

  const loading = useMemo(() => customLoading || schoolModuleLoading, [customLoading, schoolModuleLoading]);
  const isDisabled = useMemo(() =>  loading || urlLoading || fileLoading || isRemovingModule, [fileLoading, isRemovingModule, loading, urlLoading]);

  return {
    loading,
    isGroupsLoading: isGroupsLoading || deletingModuleData,
    isRemoving: isRemovingModule,
    isGroupRemoving: isSchoolGroupRemoving || deletingModuleData || isRemovingModule,
    urlLoading,
    availableGroupOptions,
    getUrl,
    clearFile,
    handleSave,
    handleRemoveModule,
    handleRemoveGroup,
    handleAddGroup,
    onChangeTab,
    onAddGroup,
    errors,
    formData,
    fileUploadingPercent,
    setFileUploadingPercent,
    fileUploadFinished,
    activeTab,
    isOpenAddModal,
    setErrors,
    fileLoading,
    groups,
    name,
    description: '',
    setName,
    setDescription: () => {},
    onCloseAddGroupModal,
    currentContent,
    data,
    setData,
    dataInputRef,
    uploadVideoAndSave,
    handleChangeVideo,
    isDisabled,
    handleContentDataSave,
    deleteGroupModuleData,
    deleteSLLGroupContent,
    deleteModuleData,
    onChangeRequiredLesson,
    onChangeLastLessonMark,
    isRequired,
    isLastLesson,
    onChangeAdditionalAfter,
    isAdditionalAfter,
    isClassPresentation: !!lesson?.id,
    isTeacherPreparationModule,
    isGroupContent,
    isPreparation,
    deleteSLLGroup
  }
}
